import type { TipoFrete } from '@/utils/frete';

interface ParametrosBarProps {
  cotacaoUsd: number;
  cotacaoEur: number;
  margemAlvo: number;
  canEditMarginTarget: boolean;
  moeda: 'BRL' | 'USD' | 'EUR';
  isentoIpi: boolean;
  isentoIcms: boolean;
  isentoPisCofins: boolean;
  condicaoPagamento: string;
  frete: TipoFrete | '';
  freteOrigem: 'cigam' | 'manual' | null;
  freteDoCadastro: boolean;
  salvarFreteNoCadastro: boolean;
  onChangeSalvarFreteNoCadastro: (v: boolean) => void;
  hasCliente: boolean;
  onChangeFrete: (v: TipoFrete) => void;
  onChangeCotacaoUsd: (v: number) => void;
  onChangeCotacaoEur: (v: number) => void;
  onChangeMargemAlvo: (v: number) => void;
  onChangeMoeda: (v: 'BRL' | 'USD' | 'EUR') => void;
  onChangeIsentoIpi: (v: boolean) => void;
  onChangeIsentoIcms: (v: boolean) => void;
  onChangeIsentoPisCofins: (v: boolean) => void;
}

export default function ParametrosBar({
  cotacaoUsd,
  cotacaoEur,
  margemAlvo,
  canEditMarginTarget,
  moeda,
  isentoIpi,
  isentoIcms,
  isentoPisCofins,
  condicaoPagamento,
  frete,
  freteOrigem,
  freteDoCadastro,
  salvarFreteNoCadastro,
  onChangeSalvarFreteNoCadastro,
  hasCliente,
  onChangeFrete,
  onChangeCotacaoUsd,
  onChangeCotacaoEur,
  onChangeMargemAlvo,
  onChangeMoeda,
  onChangeIsentoIpi,
  onChangeIsentoIcms,
  onChangeIsentoPisCofins,
}: ParametrosBarProps) {
  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      {/* Row 1: Parameters */}
      <div className="p-4 border-b border-background-100">
        <div className="flex flex-wrap items-center gap-3 lg:gap-4">
          <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
            Parâmetros
          </span>

          <div className="flex items-center gap-1.5">
            <label className="text-xs text-foreground-500 whitespace-nowrap">USD</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={cotacaoUsd}
              onChange={(e) => onChangeCotacaoUsd(parseFloat(e.target.value) || 0)}
              className="w-20 px-2 py-1.5 text-xs font-mono bg-background-50 border border-background-200/70 rounded-md text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-xs text-foreground-500 whitespace-nowrap">EUR</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={cotacaoEur}
              onChange={(e) => onChangeCotacaoEur(parseFloat(e.target.value) || 0)}
              className="w-20 px-2 py-1.5 text-xs font-mono bg-background-50 border border-background-200/70 rounded-md text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
            />
          </div>

          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full whitespace-nowrap" title="PTAX do Banco Central: último valor (fechamento) do dia útil anterior">
            <i className="ri-refresh-line" /> PTAX automática
          </span>

          {canEditMarginTarget && <div className="flex items-center gap-1.5">
            <label className="text-xs text-foreground-500 whitespace-nowrap">Margem Alvo %</label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={margemAlvo}
              onChange={(e) => onChangeMargemAlvo(parseFloat(e.target.value) || 0)}
              className="w-16 px-2 py-1.5 text-xs font-mono bg-background-50 border border-background-200/70 rounded-md text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
            />
          </div>}

          <div className="h-4 w-px bg-background-200 hidden lg:block" />

          <div className="flex items-center gap-1.5">
            <label className="text-xs text-foreground-500 whitespace-nowrap">Moeda</label>
            <div className="flex bg-background-100 rounded-lg p-0.5">
              {(['BRL', 'USD', 'EUR'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => onChangeMoeda(m)}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    moeda === m
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Tax toggles + Payment */}
      <div className="p-4 flex flex-wrap items-center gap-3 lg:gap-5">
        <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
          Isenções
        </span>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isentoIcms}
            onChange={(e) => onChangeIsentoIcms(e.target.checked)}
            className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-500/20 cursor-pointer"
          />
          <span className="text-sm text-foreground-700 whitespace-nowrap">ICMS</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isentoIpi}
            onChange={(e) => onChangeIsentoIpi(e.target.checked)}
            className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-500/20 cursor-pointer"
          />
          <span className="text-sm text-foreground-700 whitespace-nowrap">IPI</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isentoPisCofins}
            onChange={(e) => onChangeIsentoPisCofins(e.target.checked)}
            className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-500/20 cursor-pointer"
          />
          <span className="text-sm text-foreground-700 whitespace-nowrap">PIS/COFINS</span>
        </label>

        <div className="h-4 w-px bg-background-200 hidden lg:block" />

        <label className="text-xs text-foreground-500 whitespace-nowrap">Cond. Pagamento</label>
        <div className="px-3 py-1.5 min-w-28 text-sm bg-background-100 border border-background-200/70 rounded-md text-foreground-950 font-semibold" title="Definida no cadastro do cliente">
          {condicaoPagamento || 'Selecione um cliente'}
        </div>
        <span className="text-xs text-foreground-400">Cadastro do cliente</span>

        <div className="h-4 w-px bg-background-200 hidden lg:block" />

        <label className="text-xs text-foreground-500 whitespace-nowrap">Frete</label>
        <div className={`flex rounded-lg p-0.5 ${hasCliente && !frete ? 'bg-red-50 ring-1 ring-red-300' : 'bg-background-100'}`} title="CIF = frete por conta da ZSB (1-Emitente) · FOB = por conta do cliente (2-Destinatário)">
          {(['CIF', 'FOB'] as const).map((f) => (
            <button
              key={f}
              type="button"
              disabled={!hasCliente}
              onClick={() => onChangeFrete(f)}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${
                frete === f ? 'bg-primary-500 text-white shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              } ${hasCliente ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
            >
              {f}
            </button>
          ))}
        </div>
        {hasCliente && (
          <span className={`text-xs ${frete ? 'text-foreground-400' : 'text-red-600 font-medium'}`}>
            {!frete ? 'Sem frete no cadastro: escolha CIF ou FOB'
              : freteDoCadastro ? (freteOrigem === 'manual' ? 'Cadastro do cliente (manual)' : 'Cadastro do cliente')
              : 'Escolhido na cotação'}
          </span>
        )}
        {hasCliente && freteOrigem !== 'cigam' && frete && !freteDoCadastro && (
          <label className="flex items-center gap-1.5 cursor-pointer" title="O CIGAM não tem frete para este cliente. Marque para as próximas cotações já virem com esta escolha.">
            <input
              type="checkbox"
              checked={salvarFreteNoCadastro}
              onChange={(e) => onChangeSalvarFreteNoCadastro(e.target.checked)}
              className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-500/20 cursor-pointer"
            />
            <span className="text-xs text-foreground-700 whitespace-nowrap">Salvar no cadastro do cliente</span>
          </label>
        )}
      </div>
    </div>
  );
}
